import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CancelButton } from 'src/components/Buttons';
import { OkButton } from 'src/components/Buttons/OkButton';
import {
  AttachmentDisplay,
  AttachmentInfo,
} from 'src/components/Messages/AttachmentsPanel';
import { validateAttachments } from 'src/components/ReachProSupport/attachmentValidation';
import { useAppContext } from 'src/contexts/AppContext';
import { Content, useContent } from 'src/contexts/ContentContext';
import { PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useCreateReachProSupportCase } from 'src/hooks/api/useCreateReachProSupportCase';
import { AddFileIcon } from 'src/svgs/AddFileIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { SaleClient } from 'src/WebApiController';

import { Modal, ModalBody, ModalFooter, ModalHeader } from '../Modal';
import * as styles from './CreateReachProSupportCaseModal.css';

export type CreateReachProSupportCaseModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
  initialTopic?: string;
  /** Contextual placeholder shown in the message textarea (not pre-filled text). */
  initialPlaceholder?: string;
  initialEventId?: number;
  initialListingId?: number;
  initialSaleId?: number;
  onCreated?: (workItemId: number) => void;
};

function toAttachmentInfo(f: File): AttachmentInfo {
  return {
    attachmentId: f.name,
    name: f.name,
    size: f.size,
  };
}

export function CreateReachProSupportCaseModal({
  isOpen,
  onClose,
  initialTopic = '',
  initialPlaceholder,
  initialEventId,
  initialListingId,
  initialSaleId,
  onCreated,
}: CreateReachProSupportCaseModalProps) {
  const navigate = useNavigate();

  const [topic, setTopic] = useState(initialTopic);
  const [eventId, setEventId] = useState(initialEventId?.toString() ?? '');
  const [listingId, setListingId] = useState(
    initialListingId?.toString() ?? ''
  );
  const [saleId, setSaleId] = useState(initialSaleId?.toString() ?? '');
  const [files, setFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | undefined>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync state with props when modal opens
  useEffect(() => {
    if (isOpen) {
      setTopic(initialTopic);
      setEventId(initialEventId?.toString() ?? '');
      setListingId(initialListingId?.toString() ?? '');
      setSaleId(initialSaleId?.toString() ?? '');
      setFiles([]);
      setAttachmentError(undefined);
    }
  }, [isOpen, initialTopic, initialEventId, initialListingId, initialSaleId]);

  const { activeAccountWebClientConfig } = useAppContext();
  const createSupportCase = useCreateReachProSupportCase();

  // Auto-populate event ID when sale ID is entered and event ID is empty
  useEffect(() => {
    const trimmedSaleId = saleId.trim();
    if (!trimmedSaleId || eventId.trim() !== '') {
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const sales = await new SaleClient(
          activeAccountWebClientConfig
        ).getSalesByMarketplaceSaleId(trimmedSaleId);
        const stubHubSale = sales?.find((s) => s.mkp === 'StubHub');
        if (stubHubSale?.viagEvId != null) {
          setEventId(stubHubSale.viagEvId.toString());
        }
      } catch {
        // Ignore lookup failures — user can still enter event ID manually
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [saleId, eventId, activeAccountWebClientConfig]);

  const isValid =
    (topic.trim() !== '' || files.length > 0) &&
    (eventId.trim() === '' || !isNaN(parseInt(eventId)));

  const addFiles = (incoming: File[]) => {
    const result = validateAttachments(files, incoming);
    if (result.error) {
      setAttachmentError(result.error.message);
      return;
    }
    setAttachmentError(undefined);
    setFiles((prev) => [...prev, ...result.accepted]);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files ?? []);
    if (selected.length > 0) {
      addFiles(selected);
    }
    e.target.value = '';
  };

  const handleDeleteAttachment = (attachment: AttachmentInfo) => {
    setFiles((prev) => prev.filter((f) => f.name !== attachment.attachmentId));
    setAttachmentError(undefined);
  };

  const handleCreate = async () => {
    if (!isValid) {
      return;
    }

    const supportCase = await createSupportCase.mutateAsync({
      topic: topic.trim(),
      eventId: eventId.trim() ? parseInt(eventId) : undefined,
      listingId: listingId ? parseInt(listingId) : null,
      saleId: saleId ? parseInt(saleId) : null,
      attachments: files,
    });

    if (!supportCase) {
      return;
    }

    onClose?.();

    if (onCreated) {
      onCreated(supportCase.workItemId);
    } else {
      navigate(`/reachpro-support/${supportCase.workItemId}`);
    }

    setTopic('');
    setEventId('');
    setListingId('');
    setSaleId('');
    setFiles([]);
    setAttachmentError(undefined);
  };

  const handleClose = () => {
    setTopic('');
    setEventId('');
    setListingId('');
    setSaleId('');
    setFiles([]);
    setAttachmentError(undefined);
    onClose?.();
  };

  const defaultPlaceholder = useContent(ContentId.IssueWithTicketDelivery);
  const placeholderText = initialPlaceholder || defaultPlaceholder;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      unmountOnClose
      centered
      size="sm"
    >
      <ModalHeader onClose={handleClose}>
        <div className={styles.headerText}>
          <Content id={ContentId.CreateSupportConversation} />
        </div>
      </ModalHeader>
      <ModalBody>
        <div className={styles.modalBody}>
          <Stack direction="column" gap="l">
            <div className={styles.fieldLabel}>
              <Content id={ContentId.SupportMessage} />
              <span className={styles.required}>*</span>
            </div>
            <textarea
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={placeholderText}
              maxLength={2000}
              className={styles.messageTextarea}
              rows={4}
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.StubHubEventId} />
            </div>
            <PosTextField
              value={eventId}
              onChange={(e) => setEventId(e.target.value)}
              type="number"
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.StubHubListingId} />
            </div>
            <PosTextField
              value={listingId}
              onChange={(e) => setListingId(e.target.value)}
              type="number"
            />

            <div className={styles.fieldLabel}>
              <Content id={ContentId.StubHubSaleId} />
            </div>
            <PosTextField
              value={saleId}
              onChange={(e) => setSaleId(e.target.value)}
              type="number"
            />

            <input
              ref={fileInputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.pdf"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={createSupportCase.isPending}
              className={styles.browseFilesButton}
            >
              <AddFileIcon size="16px" />
              <Content id={ContentId.BrowseFiles} />
            </button>

            {files.length > 0 && (
              <Stack direction="row" gap="s" flexWrap="wrap">
                {files.map((f) => (
                  <AttachmentDisplay
                    key={f.name}
                    attachmentInfo={toAttachmentInfo(f)}
                    showDelete={true}
                    onDeleteClicked={handleDeleteAttachment}
                  />
                ))}
              </Stack>
            )}
            {attachmentError && (
              <div
                style={{
                  fontSize: vars.typography.fontSize.xs,
                  color: vars.color.textErrorV2,
                }}
              >
                {attachmentError}
              </div>
            )}
          </Stack>
        </div>
      </ModalBody>
      <ModalFooter>
        <CancelButton
          variant="regular"
          textContentId={ContentId.Cancel}
          onClick={handleClose}
        />
        <OkButton
          variant="regular"
          textContentId={ContentId.Create}
          onClick={handleCreate}
          disabled={!isValid || createSupportCase.isPending}
        />
      </ModalFooter>
    </Modal>
  );
}
